import React from 'react';
import PropTypes from 'prop-types';
// import Knob from 'react-canvas-knob';
import { Modal } from 'antd';
import { I18n as I } from 'react-redux-i18n';
import { BID_STEP } from 'lib/Constants';

import moment from 'moment';
import 'moment/locale/el';

import {
  LoaderOverlay,
  parseFieldAmount,
  formatAmount,
  formatFieldAmount,
  isFunc,
} from 'vdlib';

import {
  Checkbox,
  Form,
  Input,
  InputNumber,
  Slider,
  Row,
  Col,
  Card,
} from 'antd';

const { TextArea } = Input;

const defaultDuration = 120;

const AuctionDurationModal = ({
  marketValue,
  cancelButton,
  loading,
  onHide,
  onSubmit,
  show,
  submitButton,
  title,
  comments,
}) => {
  const [form] = Form.useForm();

  const [formData, _setFormData] = React.useState({
    marketValue: marketValue,
    stepBid: BID_STEP,
    online: false,
    comments: comments,
    dirty: false,
    duration: defaultDuration,
  });

  const [sliderValue, setSliderValue] = React.useState(defaultDuration);

  const setFormData = React.useCallback(
    (name, value) =>
      _setFormData(fd => ({ ...fd, [name]: value, dirty: true })),
    [_setFormData]
  );

  const handleSubmit = React.useCallback(() => {
    let finalDate = moment.utc();
    finalDate.add(formData.duration, 'hours');

    if (isFunc(onSubmit)) {
      onSubmit(
        finalDate.toDate(),
        formData.marketValue,
        formData.stepBid,
        formData.comments,
        formData.online
      );
    }
  }, [formData, onSubmit]);

  const handleHide = React.useCallback(() => {
    if (isFunc(onHide)) onHide();
  }, [onHide]);

  const onFormValuesChange = (changedValues, allValues) => {
    setFormData(Object.keys(changedValues)[0], Object.values(changedValues)[0]);
  };

  const createMarks = () => {
    let marks = {};

    for (let index = 24; index <= 240; index += 24) {
      const obj = {
        [index]: {
          style: {
            color: '#1373A9',
          },
          label: `${index / 24} Ημέρες`,
        },
      };

      Object.assign(marks, obj);
    }
    return marks;
  };

  const onChange = value => {
    setSliderValue(value);
    setFormData('duration', value);
  };

  return (
    <Modal
      width={700}
      onHide={handleHide}
      visible={show}
      title={title}
      okText={submitButton}
      cancelText={cancelButton}
      onCancel={handleHide}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            form.resetFields();
            handleSubmit(values);
          })
          .catch(info => {
            console.log('Validate Failed:', info);
          });
      }}
      bodyStyle={{ padding: 2 }}
    >
      <LoaderOverlay loading={loading === true} />
      <Form
        layout="vertical"
        form={form}
        name="duration-modal"
        onValuesChange={onFormValuesChange}
        initialValues={{
          marketValue: marketValue,
          comments: comments,
          stepBid: BID_STEP,
          duration: formData.duration,
        }}
        style={{ backgroundColor: '#efefef', padding: 10 }}
      >
        <Row>
          <Col span={8}>
            <Card title={I.t('DURATION')}>
              <Form.Item
                name="duration"
                label=""
                style={{ marginLeft: 22, marginBottom: 0 }}
              >
                <Row>
                  <Slider
                    style={{ display: 'inline-block', height: 320 }}
                    vertical
                    marks={createMarks()}
                    included={false}
                    step={1}
                    min={1}
                    max={240}
                    value={typeof sliderValue === 'number' ? sliderValue : 0}
                    onChange={onChange}
                  />
                </Row>
                <Row>
                  <InputNumber
                    style={{ width: 100 }}
                    min={1}
                    max={240}
                    value={sliderValue}
                    onChange={onChange}
                    prefix="Ωρες"
                    formatter={value => `${value}${I.t('HOURS')}`}
                    parser={value => value.replace(I.t('HOURS'), '')}
                  />
                </Row>
              </Form.Item>
            </Card>
          </Col>
          <Col span={16}>
            <Card
              title={I.t('AUCTION_INFO')}
              style={{ width: '100%', height: '100%' }}
            >
              <Form.Item name="online" label="" valuePropName="checked">
                <Checkbox checked={formData.isOnline}>{I.t('LIVE')}</Checkbox>
              </Form.Item>
              <Form.Item
                name="stepBid"
                label={I.t('BID_STEP')}
                rules={[
                  { required: true },
                  () => ({
                    validator(rule, value) {
                      if (!value || value % BID_STEP !== 0) {
                        return Promise.reject(
                          `Προτεινόμενα ποσά ${formatAmount(
                            (Math.floor(value / BID_STEP) + 1) * BID_STEP
                          )} ή ${formatAmount(
                            (Math.floor(value / BID_STEP) + 1) * BID_STEP -
                              BID_STEP
                          )}`
                        );
                      } else {
                        return Promise.resolve();
                      }
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  max={1000000000}
                  step={50}
                  style={{ textAlign: 'right', width: '100%' }}
                  parser={value => parseFieldAmount(value)}
                  formatter={value => formatFieldAmount(value)}
                />
              </Form.Item>
              <Form.Item
                name="marketValue"
                label={I.t('MARKET_VALUE')}
                rules={[
                  { required: true },
                  () => ({
                    validator(rule, value) {
                      if (!value || value % BID_STEP !== 0) {
                        return Promise.reject(
                          `Προτεινόμενα ποσά ${formatAmount(
                            (Math.floor(value / BID_STEP) + 1) * BID_STEP
                          )} ή ${formatAmount(
                            (Math.floor(value / BID_STEP) + 1) * BID_STEP -
                              BID_STEP
                          )}`
                        );
                      } else {
                        return Promise.resolve();
                      }
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  max={1000000000}
                  step={50}
                  style={{ textAlign: 'right', width: '100%' }}
                  parser={value => parseFieldAmount(value)}
                  formatter={value => formatFieldAmount(value)}
                />
              </Form.Item>
              <Form.Item
                name="comments"
                label={I.t('COMMENTS')}
                requiredMark="optional"
              >
                <TextArea
                  value={formData.comments}
                  placeholder="Comments"
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

AuctionDurationModal.propTypes = {
  loading: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  show: PropTypes.bool,
  theme: PropTypes.string,
  title: PropTypes.string,
};

export default AuctionDurationModal;
